import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Formik, FormikConfig, FormikErrors } from "formik";
import {
    keepPreviousData,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";

import { noop } from "@common/helpers/function";
import { CreateManualJournalEntryDto } from "@common/dto/manualJournalEntry/create-manual-journal-entry.dto";
import { ManualJournalEntryLineDto } from "@common/dto/manualJournalEntry/manual-journal-entry-line.dto";
import { JournalEntrySource } from "@common/types/domains/accounting/journalEntry";
import { Entity } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ManualJournalRecurringRuleDto } from "@common/dto/manualJournalEntry/manual-journal-recurring-rule.dto";
import { ConfirmRecurringModal } from "@components/accounting/journalEntryDrawer/ConfirmRecurringModal";
import { ManualJournalRecurringUpdate } from "@common/types/domains/accounting/manualJournalRecurringRule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConstant } from "@hooks/useConstant";
import { submitHelper } from "@helpers/form";
import { createManualJournalEntry, upsertJournalEntry } from "@lib/accounting";
import { formatDate } from "@helpers/date";
import { journalEntryContractClient } from "@api/journalEntry.api";
import { parseISO } from "date-fns";
import { JournalEntrySchema } from "@common/schemas/domains/accounting/journalEntry";
import { useIsTransactionJournalEntry } from "@components/accounting/journalEntryDrawer/JournalEntryDrawerHooks";
import { useTransactionsByIds } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { useUpdateTransactionMutation } from "@mutations/transaction";
import { accountingReportsQueryKeys } from "@api/accountingReports.api";
import { TRANSACTIONS_QUERY_KEY } from "@components/transactions/useTransactionsQuery";
import { checkSimilarTransactionsAndShowToastIfApplicable } from "@components/transactions/similarTransactionsUpdate";
import { createDefaultTransactionView } from "@components/transactions/views/DefaultTransactionView";
import { useScope } from "@state/scopeContext.lib";
import { useEntitiesById } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useOverlays } from "../../../contexts/OverlayContext";
import { JournalEntryDrawerDelete } from "./JournalEntryDrawerDelete";
import {
    JOURNAL_ENTRY_QUERY_KEY,
    removeJournalEntryQueries,
} from "./journalEntryQuery";
import { buildEmptyLines } from "./JournalEntryDrawerUtils";
import { JournalEntryDrawerContent } from "./JournalEntryDrawerContent";
import { useBothLedgersAccounts } from "./useBothLedgersAccounts";
import {
    buildJournalEntryDuplicate,
    isFilledLine,
    JournalEntryDrawerMutableConfig,
    JournalEntryDuplicateDraft,
    JournalEntryDuplicateRequest,
    validateJournalEntryForm,
} from "./JournalEntryDrawer.lib";
import {
    collectTransactionJournalEntryUpdates,
    collectUniqueLineTransactionIds,
} from "./journalEntryTransactionLines";
import { JournalEntryScope } from "./journalEntryScope";
import { JournalEntryConsolidationContext } from "./journalEntryConsolidationContext";
import {
    buildLedgerSwitchTargets,
    LedgerSwitchTarget,
    resolveLedgerSwitchTarget,
} from "./journalEntryLedgerToggle";

function formatJournalEntryDate(
    date: string | Date,
    dateOnly?: string | null,
): string {
    if (dateOnly) {
        return dateOnly;
    }
    return formatDate(date instanceof Date ? date : parseISO(date));
}

interface Props {
    show: boolean;
    onHide: () => void;
    entity: Pick<Entity, "id" | "legalType" | "name">;
    journalEntryId: string | undefined;
    ledgerBasis?: AccountingLedgerBasis;
    ledgerId?: string;
    /** Pre-fills the date field when creating a new JE (forwarded from Save and New).
     *  Must be in the same format as `formatDate()` output (e.g. "YYYY-MM-DD"). */
    initialDate?: string;
    duplicateFrom?: JournalEntryDuplicateDraft;
    scope?: JournalEntryScope;
    entities?: readonly Entity[];
    consolidationContext?: JournalEntryConsolidationContext;
    onScopeChange?: (scope: JournalEntryScope) => void;
}

export type JournalEntryLineProxyObject = Omit<
    ManualJournalEntryLineDto,
    "accountCode"
> & {
    id: string;
    excludeFrom1099?: boolean;
    transactionId?: number | null;
};

type JournalEntryFormProxyObject = Omit<
    CreateManualJournalEntryDto,
    "lines" | "memo"
> & {
    id?: string;
    memo: string;
    lines: JournalEntryLineProxyObject[];
    source: JournalEntrySource | null;
    reversedManualJournalEntryId?: string | null;
    reversingManualJournalEntryIds?: string[];
    recurringRule?: ManualJournalRecurringRuleDto;
    isCombinedSplitJournalEntry?: boolean;
};

export interface JournalEntryDrawerFormikConfig {
    journalEntry: JournalEntryFormProxyObject;
    isHideAfterSubmit: boolean;
    entityId: number;
    journalEntryId: string | undefined;
    ledgerBasis: AccountingLedgerBasis;
    ledgerId?: string;
    recurringRuleUpdateType?: ManualJournalRecurringUpdate | null;
}

export type JournalEntryDrawerErrors =
    FormikErrors<JournalEntryDrawerFormikConfig> & {
        debitAndCreditSums?: string;
    };

export const JournalEntryDrawer: React.FC<Props> = ({
    show,
    onHide,
    entity,
    journalEntryId,
    ledgerBasis,
    ledgerId,
    initialDate,
    duplicateFrom,
    scope,
    entities,
    consolidationContext,
    onScopeChange,
}) => {
    const [loading, setLoading] = useState(false);
    const [formEntityId, setFormEntityId] = useState<number>(entity.id);
    const [prevEntityId, setPrevEntityId] = useState(entity.id);
    if (entity.id !== prevEntityId) {
        setPrevEntityId(entity.id);
        setFormEntityId(entity.id);
    }
    const entitiesById = useEntitiesById({ onlyBusiness: false });
    const formEntityName = entitiesById.get(formEntityId)?.name ?? entity.name;
    const { toast, error: toastError } = useToaster();
    const { workspaceId } = useScope();
    const { enabled: isGlFirst } = useIsGlFirst();
    const { disableAiRules } = useTypedFlags();
    const onHideRef = useRef(onHide);
    const toastErrorRef = useRef(toastError);

    useEffect(() => {
        onHideRef.current = onHide;
        toastErrorRef.current = toastError;
    }, [onHide, toastError]);

    const [submittedWithErrors, setSubmittedWithErrors] = useState(false);

    const [showDeletionConfirmation, setShowDeletionConfirmation] =
        useState(false);

    const {
        formLedgerBasis,
        setFormLedgerBasis,
        effectiveLedgerBasis,
        accounts,
        accountsById,
        allowedAccountIds,
    } = useBothLedgersAccounts({
        entityId: formEntityId,
        ledgerBasis,
    });

    const accountIdSet = useMemo(
        () => new Set(accounts.map((account) => account.id)),
        [accounts],
    );

    const queryClient = useQueryClient();

    const [showRecurringOptionsModal, setShowRecurringOptionsModal] =
        useState(false);
    const journalEntryQueryLedgerId = ledgerId ?? undefined;
    // Saving a transaction journal entry regenerates it under a new id, so the
    // old id must not be refetched while the drawer closes.
    const [regeneratingJournalEntryId, setRegeneratingJournalEntryId] =
        useState<string>();
    const isJournalEntryRegenerating =
        !!journalEntryId && regeneratingJournalEntryId === journalEntryId;

    const fetchedJournalEntry = useQuery({
        queryKey: [
            JOURNAL_ENTRY_QUERY_KEY,
            journalEntryId,
            entity.id,
            journalEntryQueryLedgerId,
        ],
        queryFn: async () => {
            if (!journalEntryId) {
                return null;
            }
            const response = await journalEntryContractClient.getJournalEntry({
                params: {
                    entityId: entity.id,
                    id: journalEntryId,
                },
                query: {
                    ledgerBasis:
                        effectiveLedgerBasis ?? AccountingLedgerBasis.cash,
                    ledgerId: journalEntryQueryLedgerId,
                },
            });

            if (response.status !== 200) {
                throw new Error("Journal entry not found");
            }

            const je = JournalEntrySchema.parse(response.body);
            const lines = buildEmptyLines();

            for (const [index, line] of je.lines.entries()) {
                lines[index] = {
                    id: Math.random().toString(),
                    accountId: line.accountId,
                    debitAmount: line.debitAmount,
                    creditAmount: line.creditAmount,
                    description: line.description,
                    classIds: line.classIds,
                    counterpartyId: line.counterpartyId,
                    excludeFrom1099: line.excludeFrom1099,
                    transactionId: line.transactionId,
                };
            }
            return {
                id: je.id,
                date: formatJournalEntryDate(je.date, je.dateOnly),
                memo: je.memo ?? "",
                lines,
                source: je.source,
                financialDocumentIds: je.financialDocumentIds,
                reversedAt: je.reversedAt
                    ? formatJournalEntryDate(je.reversedAt)
                    : null,
                reversedManualJournalEntryId: je.reversedManualJournalEntryId,
                reversingManualJournalEntryIds:
                    je.reversingManualJournalEntryIds,
                recurringRule: je.recurringRule
                    ? {
                          ...je.recurringRule,
                          endDate: je.recurringRule.endDate
                              ? formatDate(parseISO(je.recurringRule.endDate))
                              : null,
                      }
                    : undefined,
                ledgerBasis: je.ledgerBasis ?? AccountingLedgerBasis.cash,
                ledgerId: je.accountingLedgerId,
                isCombinedSplitJournalEntry:
                    je.isCombinedSplitJournalEntry ?? false,
            };
        },
        enabled: !isJournalEntryRegenerating,
        placeholderData: isJournalEntryRegenerating
            ? keepPreviousData
            : undefined,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: false,
    });

    useEffect(() => {
        if (fetchedJournalEntry.isError) {
            toastErrorRef.current(
                "Journal entry not found or has been deleted",
            );
            onHideRef.current();
        }
    }, [fetchedJournalEntry.isError]);

    const today = useConstant(() => formatDate(new Date()));
    const memoizedInitialValues: JournalEntryDrawerFormikConfig = useMemo(
        () => ({
            journalEntry: fetchedJournalEntry.data ?? {
                date: duplicateFrom?.date ?? initialDate ?? today,
                memo: duplicateFrom?.memo ?? "",
                lines: duplicateFrom?.lines ?? buildEmptyLines(),
                source: null,
                ledgerBasis: effectiveLedgerBasis ?? AccountingLedgerBasis.cash,
            },
            isHideAfterSubmit: false,
            entityId: entity.id,
            journalEntryId,
            ledgerBasis: effectiveLedgerBasis ?? AccountingLedgerBasis.cash,
            ledgerId:
                fetchedJournalEntry.data?.ledgerId ?? duplicateFrom?.ledgerId,
        }),
        [
            fetchedJournalEntry.data,
            entity.id,
            journalEntryId,
            effectiveLedgerBasis,
            initialDate,
            duplicateFrom,
            today,
        ],
    );

    const onDeleteSuccess = useCallback(() => {
        onHide();
        setShowDeletionConfirmation(false);
    }, [onHide]);

    const isTransactionJournal = useIsTransactionJournalEntry(
        memoizedInitialValues,
    );

    const transactionIdsToLoad = useMemo(() => {
        const fromLines = collectUniqueLineTransactionIds(
            memoizedInitialValues.journalEntry.lines,
        );
        if (fromLines.length > 0) {
            return fromLines;
        }
        const sourceId = memoizedInitialValues.journalEntry.source?.id;
        if (isTransactionJournal && typeof sourceId === "number") {
            return [sourceId];
        }
        return [];
    }, [
        isTransactionJournal,
        memoizedInitialValues.journalEntry.lines,
        memoizedInitialValues.journalEntry.source?.id,
    ]);

    const { transactions: journalEntryTransactions } =
        useTransactionsByIds(transactionIdsToLoad);

    const updateTransaction = useUpdateTransactionMutation();
    const { openOverlay } = useOverlays();

    // Transaction-sourced JEs post one 2-line entry per ledger the
    // transaction participates in (cash always; accrual when an override
    // account exists for it). Drilling down from a transaction previously
    // locked the ledger selector entirely; collect each ledger's
    // referenceNumber here so the user can toggle to the sibling entry when
    // one exists, instead of being stuck on whichever ledger was opened.
    const ledgerSwitchTargets = useMemo(
        () =>
            isTransactionJournal
                ? buildLedgerSwitchTargets({
                      transactions: journalEntryTransactions,
                      entityId: entity.id,
                  })
                : new Map<string, LedgerSwitchTarget>(),
        [isTransactionJournal, journalEntryTransactions, entity.id],
    );

    const currentLedgerId = ledgerId ?? fetchedJournalEntry.data?.ledgerId;

    const onSwitchLedger = useCallback(
        (targetLedgerId: string) => {
            const target = resolveLedgerSwitchTarget({
                ledgerSwitchTargets,
                targetLedgerId,
                currentLedgerId,
            });
            if (!target) {
                return;
            }
            openOverlay("journalEntryDrawer", {
                entity: entitiesById.get(entity.id) ?? entity,
                journalEntryId: target.referenceNumber,
                ledgerId: targetLedgerId,
                ledgerBasis: target.ledgerBasis,
            });
        },
        [
            ledgerSwitchTargets,
            currentLedgerId,
            openOverlay,
            entitiesById,
            entity,
        ],
    );

    const onDuplicate = useCallback(
        ({ values, lockDate }: JournalEntryDuplicateRequest) => {
            const draft = buildJournalEntryDuplicate({
                ...values,
                lockDate,
                today: formatDate(new Date()),
            });
            openOverlay("journalEntryDrawer", {
                entity: entitiesById.get(values.entityId) ?? entity,
                ledgerBasis: values.ledgerBasis,
                duplicateFrom: draft,
            });
        },
        [entitiesById, entity, openOverlay],
    );

    const form: FormikConfig<JournalEntryDrawerFormikConfig> = useMemo(
        () => ({
            validateOnBlur: true,
            validateOnChange: true,
            initialValues: memoizedInitialValues,
            enableReinitialize: true,
            validate: (values) => {
                const validationResult = validateJournalEntryForm({
                    values,
                    accountIdSet,
                    submittedWithErrors,
                    forceValidate: JournalEntryDrawerMutableConfig.validate,
                });

                if (validationResult.shouldMarkSubmittedWithErrors) {
                    setSubmittedWithErrors(true);
                }
                if (validationResult.didConsumeForceValidate) {
                    JournalEntryDrawerMutableConfig.validate = false;
                }

                return validationResult.errors as JournalEntryDrawerErrors;
            },
            onSubmit: submitHelper({
                loading,
                setLoading,
                handler: async (
                    {
                        journalEntry,
                        isHideAfterSubmit,
                        entityId,
                        ledgerBasis: innerLedgerBasis,
                        ledgerId: selectedLedgerId,
                        recurringRuleUpdateType,
                    },
                    { validateForm, resetForm },
                ) => {
                    const errors: JournalEntryDrawerErrors = await validateForm(
                        {
                            journalEntry,
                        },
                    );

                    if (Object.keys(errors).length > 0) {
                        setSubmittedWithErrors(true);
                        return;
                    }

                    const shouldShowRecurringOptionsModal =
                        !recurringRuleUpdateType &&
                        !!journalEntry.recurringRule?.period &&
                        journalEntryId;

                    if (shouldShowRecurringOptionsModal) {
                        setShowRecurringOptionsModal(true);
                        return;
                    }

                    const isCreating = !journalEntryId;
                    const journalEntryDto: CreateManualJournalEntryDto = {
                        memo:
                            journalEntry.memo.length > 0
                                ? journalEntry.memo
                                : null,
                        lines: journalEntry.lines
                            .filter(isFilledLine)
                            .map((line) => ({
                                debitAmount: line.debitAmount ?? null,
                                creditAmount: line.creditAmount ?? null,
                                accountCode: accountsById[line.accountId].code,
                                accountId: line.accountId,
                                description:
                                    line.description &&
                                    line.description.length > 0
                                        ? line.description
                                        : "Manual journal",
                                classIds: line.classIds,
                                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                counterpartyId: line.counterpartyId || null,
                                excludeFrom1099: line.excludeFrom1099 ?? false,
                            })),
                        date: journalEntry.date,
                        ledgerBasis: isCreating
                            ? (formLedgerBasis ?? AccountingLedgerBasis.cash)
                            : (innerLedgerBasis ?? AccountingLedgerBasis.cash),
                        ledgerId: selectedLedgerId,
                        financialDocumentIds: journalEntry.financialDocumentIds,
                        reversedAt: journalEntry.reversedAt,
                        recurringRule: journalEntry.recurringRule?.period
                            ? journalEntry.recurringRule
                            : undefined,
                        recurringRuleUpdateType,
                    };

                    if (isTransactionJournal) {
                        const transactionUpdates =
                            collectTransactionJournalEntryUpdates({
                                lines: journalEntry.lines,
                                initialLines:
                                    memoizedInitialValues.journalEntry.lines,
                                memo: journalEntry.memo,
                                initialMemo:
                                    memoizedInitialValues.journalEntry.memo,
                                transactions: journalEntryTransactions,
                                ledgerBasis:
                                    innerLedgerBasis ??
                                    AccountingLedgerBasis.cash,
                            });

                        let hasCommittedTransactionUpdate = false;
                        if (transactionUpdates.length > 0) {
                            setRegeneratingJournalEntryId(journalEntryId);
                        }
                        const commitTransactionUpdates = async () => {
                            for (const {
                                transaction,
                                update,
                            } of transactionUpdates) {
                                const response =
                                    await updateTransaction.mutateAsync({
                                        transaction,
                                        update,
                                    });
                                hasCommittedTransactionUpdate = true;
                                if (
                                    "transaction" in response &&
                                    workspaceId &&
                                    !disableAiRules
                                ) {
                                    const newAccountId =
                                        update.accountId ??
                                        update.accrualAccountId ??
                                        undefined;
                                    checkSimilarTransactionsAndShowToastIfApplicable(
                                        {
                                            workspaceId,
                                            response,
                                            payload: update,
                                            transactionBeforeUpdate:
                                                transaction,
                                            toast,
                                            displayedChangedEntityName:
                                                newAccountId
                                                    ? accountsById[newAccountId]
                                                          ?.name
                                                    : undefined,
                                            view: createDefaultTransactionView({
                                                entityIds: [entity.id],
                                                hasAnyClass: true,
                                                isGlFirst,
                                            }),
                                        },
                                    ).catch(noop);
                                }
                            }
                            if (transactionUpdates.length > 0) {
                                toast(
                                    `Transaction updated for ${formEntityName}`,
                                );
                            }
                        };
                        await commitTransactionUpdates().finally(() => {
                            if (hasCommittedTransactionUpdate) {
                                queryClient.invalidateQueries({
                                    queryKey: [TRANSACTIONS_QUERY_KEY],
                                });
                            } else {
                                setRegeneratingJournalEntryId(undefined);
                            }
                        });
                    } else if (journalEntryId) {
                        await upsertJournalEntry(
                            entityId,
                            journalEntryId,
                            journalEntryDto,
                        );
                        toast(`Journal entry updated for ${formEntityName}`);
                    } else {
                        await createManualJournalEntry(
                            entityId,
                            journalEntryDto,
                        );
                        toast(`Journal entry created for ${formEntityName}`);
                    }

                    if (isHideAfterSubmit) {
                        onHide();
                    } else {
                        openOverlay("journalEntryDrawer", {
                            entity: entitiesById.get(entityId) ?? entity,
                            ledgerBasis,
                            initialDate: journalEntry.date,
                        });
                    }

                    resetForm();
                    setSubmittedWithErrors(false);

                    queryClient.invalidateQueries({
                        queryKey: [
                            accountingReportsQueryKeys.accountTransactions,
                        ],
                    });
                    for (const reportQueryKey of [
                        accountingReportsQueryKeys.balanceSheet,
                        accountingReportsQueryKeys.trialBalance,
                        accountingReportsQueryKeys.profitAndLoss,
                        accountingReportsQueryKeys.cashFlowStatement,
                    ]) {
                        queryClient.invalidateQueries({
                            queryKey: [reportQueryKey],
                        });
                    }

                    if (isTransactionJournal) {
                        queryClient.invalidateQueries({
                            queryKey: [TRANSACTIONS_QUERY_KEY],
                        });
                    }
                    removeJournalEntryQueries(journalEntryId);
                },
            }),
        }),
        [
            memoizedInitialValues,
            loading,
            submittedWithErrors,
            accountIdSet,
            journalEntryId,
            queryClient,
            toast,
            entity,
            onHide,
            accountsById,
            updateTransaction,
            journalEntryTransactions,
            isTransactionJournal,
            entitiesById,
            openOverlay,
            ledgerBasis,
            formLedgerBasis,
            workspaceId,
            formEntityName,
            isGlFirst,
            disableAiRules,
        ],
    );

    return (
        <Formik {...form}>
            {({ setFieldValue, handleSubmit }) => (
                <>
                    <JournalEntryDrawerDelete
                        onBack={() => setShowDeletionConfirmation(false)}
                        onDeleteSuccess={onDeleteSuccess}
                        onHide={() => setShowDeletionConfirmation(false)}
                        show={show && showDeletionConfirmation}
                    />

                    <JournalEntryDrawerContent
                        open={show && !showDeletionConfirmation}
                        onClose={onHide}
                        formEntityId={formEntityId}
                        setFormEntityId={setFormEntityId}
                        ledgerSelection={
                            formLedgerBasis ?? AccountingLedgerBasis.cash
                        }
                        setFormLedgerBasis={setFormLedgerBasis}
                        loading={loading}
                        onDelete={() => setShowDeletionConfirmation(true)}
                        onDuplicate={onDuplicate}
                        allowedAccountIds={allowedAccountIds}
                        scope={scope}
                        entities={entities}
                        consolidationContext={consolidationContext}
                        onScopeChange={onScopeChange}
                        ledgerSwitchTargets={ledgerSwitchTargets}
                        onSwitchLedger={onSwitchLedger}
                    />

                    <ConfirmRecurringModal
                        title="You're changing a recurring journal entry"
                        subtitle="Please select what you'd like to update."
                        show={showRecurringOptionsModal}
                        onSubmit={(recurringRuleUpdateType) => {
                            setShowRecurringOptionsModal(false);
                            setFieldValue(
                                "recurringRuleUpdateType",
                                recurringRuleUpdateType,
                            );
                            handleSubmit();
                        }}
                        onCancel={() => setShowRecurringOptionsModal(false)}
                    />
                </>
            )}
        </Formik>
    );
};
